import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../api/client";
import { useAuth } from "./AuthContext";

// "Operando como" — define o contexto atual da sessão (Pessoa Física ou empresa).
// Usado como default em: checkout (Alugar como), publicar (Anunciar como) e
// futuro filtro do painel locador.
//
// Persiste a escolha em localStorage por usuário (`toalugando_operando_como_<uid>`)
// e mantém um snapshot global em `toalugando_op_ctx` que o api client lê pra
// anexar `?empresa_id=X` ou `?como_pf=true` automaticamente nos endpoints relevantes.
const OperandoComoContext = createContext(null);

export const useOperandoComo = () => useContext(OperandoComoContext);

const _key = (uid) => uid ? `toalugando_operando_como_${uid}` : null;

export const OperandoComoProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.id || null;

  const [empresas, setEmpresas] = useState([]);
  const [atualId, setAtualId] = useState(null); // null = PF; senão, empresa.id
  const [carregando, setCarregando] = useState(false);

  const recarregarEmpresas = useCallback(async () => {
    if (!userId) { setEmpresas([]); setAtualId(null); return; }
    setCarregando(true);
    try {
      const r = await api.fetch('/usuarios/me/empresas');
      const d = await r.json();
      const lista = Array.isArray(d?.empresas) ? d.empresas : [];
      setEmpresas(lista);
      try {
        const saved = localStorage.getItem(_key(userId));
        if (saved && saved !== 'pf' && lista.some((e) => e.id === saved)) {
          setAtualId(saved);
        }
      } catch {}
    } catch { setEmpresas([]); }
    finally { setCarregando(false); }
  }, [userId]);

  useEffect(() => {
    if (!userId) { setEmpresas([]); setAtualId(null); return; }
    recarregarEmpresas();
  }, [userId, recarregarEmpresas]);

  // Listener pra refresh externo (ex: após upload de logo)
  useEffect(() => {
    const handler = () => recarregarEmpresas();
    window.addEventListener('toalugando:empresas:refresh', handler);
    return () => window.removeEventListener('toalugando:empresas:refresh', handler);
  }, [recarregarEmpresas]);

  const mudar = useCallback((novoId) => {
    setAtualId(novoId);
    try {
      const k = _key(userId);
      if (k) localStorage.setItem(k, novoId || 'pf');
      // Snapshot global lido pelo api client em chamadas que filtram por contexto
      const ctx = novoId ? { empresa_id: novoId } : { como_pf: true };
      localStorage.setItem('toalugando_op_ctx', JSON.stringify(ctx));
      // Notifica qualquer listener (telas que precisam re-fetch ao mudar de identidade)
      window.dispatchEvent(new CustomEvent('toalugando:op:atual', { detail: ctx }));
    } catch {}
  }, [userId]);

  // Sincroniza o snapshot inicial pro api client quando o atualId carrega
  useEffect(() => {
    try {
      const ctx = atualId ? { empresa_id: atualId } : { como_pf: true };
      localStorage.setItem('toalugando_op_ctx', JSON.stringify(ctx));
    } catch {}
  }, [atualId]);

  const empresaAtual = empresas.find((e) => e.id === atualId) || null;

  const value = {
    atualId,
    empresaAtual,
    empresas,
    carregando,
    mudar,
    recarregar: recarregarEmpresas,
    estaComoPF: !empresaAtual,
    estaComoPJ: !!empresaAtual,
    nomeAtual: empresaAtual
      ? (empresaAtual.razao_social || empresaAtual.nome_fantasia || 'Empresa')
      : (user?.nome || 'Você'),
  };

  return <OperandoComoContext.Provider value={value}>{children}</OperandoComoContext.Provider>;
};
